import { LowStockIcon, TypographyV3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { useGate } from 'statsig-react';

import { EventType } from '@/analytics/types';

import ChooseButton from '../_components/ChooseButton';
import ChooseButtonGroup from '../_components/ChooseButton/ChooseButtonGroup';
import { IN_STOCK, LOW_STOCK, OUT_OF_STOCK, RING_SIZES } from '../_consts';
import { useWizardStateContext } from '../_contexts/WizardStateProvider';
import type { RingSpecificSizes } from '../_types/inventoryTypes';
import Selections from './enums/SizeStep';

const soldOutTValues = {
  amLink: (chunks: ReactNode) => (
    <a
      href="https://www.amazon.com/stores/Oura/page/9F826A80-FD70-4B6A-A1B9-188DAF83750A?ref_=ast_bln"
      className="underline"
      rel="noopener noreferrer"
      target="_blank"
    >
      {chunks}
    </a>
  ),
  bbyLink: (chunks: ReactNode) => (
    <a
      href="https://www.bestbuy.com/site/brands/oura/pcmcat1680720122530.c?id=pcmcat1680720122530"
      className="underline"
      rel="noopener noreferrer"
      target="_blank"
    >
      {chunks}
    </a>
  ),
  targLink: (chunks: ReactNode) => (
    <a
      href="https://www.target.com/b/oura/-/N-q643ler9uhu"
      className="underline"
      rel="noopener noreferrer"
      target="_blank"
    >
      {chunks}
    </a>
  ),
};

interface SizeStepRingSizeChoicesProps {
  readonly selectedSize: string;
  readonly onSelectSize: (size: string) => void;
  readonly ringSizeData: RingSpecificSizes[] | undefined;
  readonly shippingDelayValue: number;
  readonly subtextHref: React.RefObject<HTMLDivElement | null>;
}

const SizeStepRingSizeChoices = ({
  selectedSize,
  onSelectSize,
  ringSizeData,
  shippingDelayValue,
  subtextHref,
}: SizeStepRingSizeChoicesProps): JSX.Element => {
  const { hasMaxRingCount, sizingSelector, isOuraRing4Wizard, stockStatus } =
    useWizardStateContext();
  const t = useTranslations();
  const isUS = useGate('usa_only').value;
  const analyticsCTA =
    stockStatus == OUT_OF_STOCK
      ? 'Sold out online. Shop the newest Oura Ring 4 here'
      : 'Limited quantities available. Shop the newest Oura Ring 4 here.';
  const path = usePathname();

  const handleAnalytics = async () => {
    await window.ouraAnalytics.track(EventType.LinkClicked, {
      cta: analyticsCTA,
      action: 'Goes to PDP Wiz Gen 4',
      location: 'size',
      path,
    });
  };
  const OR4OOSMessaging = isUS
    ? t.rich('out_of_stock_retailers', soldOutTValues)
    : t('inventory_out_of_stock_message');
  return (
    <>
      {sizingSelector === Selections.ChooseSize && (
        <>
          {isOuraRing4Wizard && (
            <div>
              <TypographyV3 className="pt-7 text-base">
                {t.rich('or4_size_step_selection')}
              </TypographyV3>
            </div>
          )}
          <ChooseButtonGroup
            hideLabel
            label="choose_size"
            value={RING_SIZES.includes(selectedSize) ? selectedSize : null}
            onChange={onSelectSize}
          >
            <div className={cx('grid grid-cols-4 gap-4')}>
              {ringSizeData?.map(
                ({ size, stockStatus: ringStockStatus }: RingSpecificSizes) => (
                  <Fragment key={size}>
                    <ChooseButton
                      className={cx('flex justify-center align-middle', {
                        'p-5':
                          ringStockStatus === IN_STOCK ||
                          ringStockStatus === OUT_OF_STOCK,
                        'flex-col': ringStockStatus === LOW_STOCK,
                        'bg-gradient-to-tl from-transparent from-[49.5%] via-black via-[50%] to-[50.5%]':
                          ringStockStatus === OUT_OF_STOCK,
                      })}
                      data-cy={`choose-button-${size}`}
                      key={size}
                      value={size.toString()}
                      isDisabled={hasMaxRingCount}
                    >
                      {ringStockStatus === LOW_STOCK && (
                        <div
                          className="flex justify-end pr-1 pt-1 align-top"
                          data-cy="sizeButtonLowStockIcon"
                        >
                          <LowStockIcon className="text-red" />
                        </div>
                      )}
                      <div
                        className={cx({
                          'flex justify-center px-5 pb-5 pt-1 align-middle':
                            ringStockStatus === LOW_STOCK,
                        })}
                      >
                        {size}
                      </div>
                    </ChooseButton>
                  </Fragment>
                ),
              )}
            </div>
            {/* TODO: GROW-1607 Clean up when OR3 is completely OOS */}
            {stockStatus === OUT_OF_STOCK && (
              <div ref={subtextHref}>
                <TypographyV3
                  className="col-start-1 col-end-4 text-red"
                  data-cy="outOfStockSubtext"
                >
                  {isOuraRing4Wizard
                    ? OR4OOSMessaging
                    : t.rich('inventory_insights_out_of_stock_shop_gen4', {
                        link: function link(chunks: ReactNode) {
                          return (
                            <Link
                              href="/product/rings/oura-ring-4/silver/"
                              className="text-sandstone-500 underline"
                              onClick={handleAnalytics}
                            >
                              {chunks}
                            </Link>
                          );
                        },
                      })}
                </TypographyV3>
              </div>
            )}
            {stockStatus === LOW_STOCK && (
              <div ref={subtextHref}>
                <span className="flex">
                  <div className="pr-2 pt-[5px]" data-cy="subtextLowStockIcon">
                    <LowStockIcon className="text-red" />
                  </div>
                  <TypographyV3
                    className="max-w-72 text-red"
                    data-cy="lowStockSubtext"
                  >
                    {/* TODO: GROW-1607 Clean up when OR3 is completely OOS */}
                    {isOuraRing4Wizard
                      ? t('inventory_low_stock_message', {
                          amount: shippingDelayValue,
                        })
                      : t.rich('inventory_insights_low_stock_shop_gen4', {
                          link: function link(chunks: ReactNode) {
                            return (
                              <Link
                                href="/product/rings/oura-ring-4/silver/"
                                className="text-sandstone-500 underline"
                                onClick={handleAnalytics}
                                data-cy="hereLowStockLink"
                              >
                                {chunks}
                              </Link>
                            );
                          },
                        })}
                  </TypographyV3>
                </span>
              </div>
            )}
          </ChooseButtonGroup>
        </>
      )}
    </>
  );
};

export default SizeStepRingSizeChoices;
